import {
	type AllModifiersType,
	type KeywordsType,
	type SDK_Colors,
	getEnglishToLocaleKeywordMap,
	getEnglishToLocaleModifierMap,
	keywordsObject,
	measures,
	modifierObject,
	text,
} from "@quickpoint.me/shared";
import { memo, useCallback, useMemo, useState, useRef, useEffect } from "react";
import { useRecoilValue } from "recoil";
import styled from "styled-components";
import { fireInsertWordEvent } from "../../lib/editor/events";
import { CurrentFilterColorsAtom, CurrentKeyword } from "../../state/atoms";
import { LocaleAtom } from "../../state/localeAtom";
import { IsInPortrait } from "../../state/selectors";
import ColorSwatch from "./ColorSwatch";
import VocabToken from "./VocabToken";

// Data-driven selection based on actual usage from 619 stories (42K lines)
// Ordered by frequency: image (4%), title (2.5%), subtitle (1.3%),
// * bullets (0.7%), # numbered (0.6%), background (0.9%), filter (0.6%)
const TOP_LEVEL_KEYWORDS: KeywordsType[] = [
	"title:",
	"image:",
	"*:",
	"#:",
	"background:",
	"filter:",
];

// Control chars: === (14%), --- (1.2%), | (1.1%)
const TOP_LEVEL_CONTROL_CHARS = ["===", "---", "|"] as const;

const StyledVocabBar = styled.div<{ $isPortrait: boolean }>`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  min-width: 0;
  overflow: hidden;
  /* landscape: fill the grid row so the tokens center instead of
     top-aligning with dead space below */
  height: ${(props) =>
		props.$isPortrait ? measures.vocabBar.portrait.height : "100%"};
  background-color: ${(props) => props.theme.backgroundColor};
`;

const StyledTokenContainer = styled.div<{ $isPortrait: boolean; $fadeAmount: number }>`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  padding-left: ${(props) => props.$isPortrait ? "2svw" : measures.editor.landscape.gutter};
  padding-right: 1svw;
  gap: ${(props) =>
		props.$isPortrait
			? measures.vocabBar.portrait.gap
			: measures.vocabBar.landscape.gap};
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;

  /* Hide scrollbar */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }

  /* Fade effect on right edge - fades out as you scroll to end */
  -webkit-mask-image: ${(props) => props.$fadeAmount > 0.05
    ? `linear-gradient(to right, #000 0%, #000 ${100 - (30 * props.$fadeAmount)}%, transparent 100%)`
    : 'none'};
  mask-image: ${(props) => props.$fadeAmount > 0.05
    ? `linear-gradient(to right, #000 0%, #000 ${100 - (30 * props.$fadeAmount)}%, transparent 100%)`
    : 'none'};
`;

/** Maps English keyword (without colon) to text.vocabBar key */
const keywordHelpKey: Record<string, keyof typeof text.vocabBar> = {
	"*": "bullet",
	"**": "indentBullet",
	"#": "numberedList",
	"##": "indentNumbered",
	"audio": "audio",
	"autoplay": "autoplay",
	"autospace": "autospace",
	"background": "background",
	"blank": "blank",
	"box": "box",
	"button": "button",
	"chart": "chart",
	"columnTitle": "columnTitle",
	"filter": "filter",
	"form": "form",
	"image": "image",
	"line": "line",
	"map": "map",
	"moveBy": "moveBy",
	"paragraph": "paragraph",
	"read": "read",
	"storyName": "storyName",
	"subtitle": "subtitle",
	"supertitle": "supertitle",
	"pause": "pause",
	"title": "title",
	"video": "video",
};

/** Maps English modifier (without colon) to text.vocabBar key */
const modifierHelpKey: Record<string, keyof typeof text.vocabBar> = {
	"align": "modAlign",
	"across": "modAcross",
	"anchor": "modAnchor",
	"animate": "modAnimate",
	"animateStart": "modAnimateStart",
	"background": "modBackground",
	"backlink": "modBacklink",
	"border-color": "modBorderColor",
	"border-thickness": "modBorderThickness",
	"blur": "modBlur",
	"caption": "modCaption",
	"chart-type": "modChartType",
	"color": "modColor",
	"columns": "modColumns",
	"corner-radius": "modCornerRadius",
	"decoration": "modDecoration",
	"delay": "modDelay",
	"down": "modDown",
	"effect": "modEffect",
	"font": "modFont",
	"hideOn": "modHideOn",
	"loop": "modLoop",
	"toPage": "modToPage",
	"height": "modHeight",
	"line-height": "modLineHeight",
	"mailto": "modMailto",
	"mute": "modMute",
	"opacity": "modOpacity",
	"padding": "modPadding",
	"padding-bottom": "modPaddingBottom",
	"padding-left": "modPaddingLeft",
	"padding-right": "modPaddingRight",
	"padding-top": "modPaddingTop",
	"size": "modSize",
	"shadow": "modShadow",
	"url": "modUrl",
	"weight": "modWeight",
	"widescreen": "modWidescreen",
	"width": "modWidth",
};

function getLocalizedKeywordHelp(keyword: KeywordsType): string {
	const name = keyword.replace(/:$/, "");
	const key = keywordHelpKey[name];
	return key ? text.vocabBar[key] : keywordsObject[keyword].help;
}

function getLocalizedModifierHelp(modifier: AllModifiersType): string {
	const name = modifier.replace(/:$/, "");
	const key = modifierHelpKey[name];
	return key ? text.vocabBar[key] : modifierObject[modifier].help;
}

function getLocalizedKeywordLabel(keyword: KeywordsType, kwMap: Record<string, string>): string {
	const name = keyword.replace(/:$/, "");
	const translated = kwMap[name];
	return translated ? `${translated}:` : keyword;
}

function getLocalizedModifierLabel(modifier: AllModifiersType, modMap: Record<string, string>): string {
	const name = modifier.replace(/:$/, "");
	const translated = modMap[name];
	return translated ? `${translated}:` : modifier;
}

const COLOR_MODIFIERS: AllModifiersType[] = [
	"color:",
	"background:",
	"border-color:",
];

function getColorPreview(
	modifier: AllModifiersType,
	filterColors: SDK_Colors | undefined,
) {
	if (!filterColors || !COLOR_MODIFIERS.includes(modifier)) {
		return undefined;
	}
	const colorKeys = ["first", "second", "third"] as const;
	const previewColors = colorKeys
		.map((key) => filterColors?.[key])
		.filter(Boolean) as string[];

	return previewColors.length > 0 ? (
		<ColorSwatch colors={previewColors} />
	) : undefined;
}

const MemoizedVocabToken = memo(VocabToken);

function VocabBar() {
	const currentKeyword = useRecoilValue(CurrentKeyword);
	const isPortrait = useRecoilValue(IsInPortrait);
	const locale = useRecoilValue(LocaleAtom);
	// Use the stable atom that only updates when filter actually changes via message
	const filterColors = useRecoilValue(
		CurrentFilterColorsAtom,
	) as SDK_Colors | null;

	const kwMap = useMemo(() => getEnglishToLocaleKeywordMap(locale), [locale]);
	const modMap = useMemo(() => getEnglishToLocaleModifierMap(locale), [locale]);

	const containerRef = useRef<HTMLDivElement>(null);
	const [fadeAmount, setFadeAmount] = useState(1);

	const updateFade = useCallback(() => {
		const el = containerRef.current;
		if (!el) return;

		const maxScroll = el.scrollWidth - el.clientWidth;
		if (maxScroll <= 0) {
			setFadeAmount(0); // No overflow, no fade
		} else {
			const remaining = maxScroll - el.scrollLeft;
			const fade = Math.min(1, remaining / 50); // Fade out over last 50px
			setFadeAmount(fade);
		}
	}, []);

	useEffect(() => {
		updateFade();
		// Also update on resize
		window.addEventListener('resize', updateFade);
		return () => window.removeEventListener('resize', updateFade);
	}, [updateFade, currentKeyword]);

	const handleScroll = useCallback(() => {
		updateFade();
	}, [updateFade]);

	const handleTokenClick = useCallback((token: string, isModifier: boolean) => {
		// control chars (===, ---, |) have no colon and no translation; they
		// go on a line of their own
		const isControlChar = !token.endsWith(":");
		if (isControlChar) {
			fireInsertWordEvent({
				word: token as KeywordsType,
				insertAtLineNumber: false,
				newLine: true,
			});
			return;
		}

		// Translate to locale so the inserted keyword matches the editor's locale
		const name = token.replace(/:$/, "");
		const map = isModifier ? modMap : kwMap;
		const localized = map[name];
		const insertToken = localized ? `${localized}:` : token;
		fireInsertWordEvent({
			word: insertToken as KeywordsType | AllModifiersType,
			insertAtLineNumber: false,
			isModifier,
		});
	}, [kwMap, modMap]);

	const topLevelTokens = useMemo(() => {
		return (
			<>
				{TOP_LEVEL_KEYWORDS.filter((k) => !keywordsObject[k].hide).map(
					(keyword) => (
						<MemoizedVocabToken
							key={keyword}
							label={getLocalizedKeywordLabel(keyword, kwMap)}
							help={getLocalizedKeywordHelp(keyword)}
							onClick={() => handleTokenClick(keyword, false)}
							variant="keyword"
							isPortrait={isPortrait}
						/>
					),
				)}
				{TOP_LEVEL_CONTROL_CHARS.map((char) => (
					<MemoizedVocabToken
						key={char}
						label={char}
						help={
							char === "==="
								? text.vocabBar.newPage
								: char === "---"
									? text.vocabBar.gridRow
									: text.vocabBar.splitPage
						}
						onClick={() => handleTokenClick(char, false)}
						variant="control"
						isPortrait={isPortrait}
					/>
				))}
			</>
		);
	}, [isPortrait, handleTokenClick, kwMap, locale]);

	const modifierTokens = useMemo(() => {
		if (currentKeyword === "init" || currentKeyword === "topLevel") {
			return null;
		}
		const modifiers = keywordsObject[currentKeyword].modifiers;
		return modifiers
			.filter((m) => !modifierObject[m].hide)
			.map((modifier) => (
				<MemoizedVocabToken
					key={modifier}
					label={getLocalizedModifierLabel(modifier, modMap)}
					help={getLocalizedModifierHelp(modifier)}
					onClick={() => handleTokenClick(modifier, true)}
					variant="modifier"
					preview={getColorPreview(modifier, filterColors ?? undefined)}
					isPortrait={isPortrait}
				/>
			));
	}, [currentKeyword, isPortrait, handleTokenClick, filterColors, modMap, locale]);

	if (currentKeyword === "init" || currentKeyword === "topLevel") {
		return (
			<StyledVocabBar
				$isPortrait={isPortrait}
				className="vocab-bar"
				role="toolbar"
				aria-label={text.vocabBar.barLabel}
			>
				<StyledTokenContainer
					ref={containerRef}
					onScroll={handleScroll}
					$isPortrait={isPortrait}
					$fadeAmount={fadeAmount}
				>
					{topLevelTokens}
				</StyledTokenContainer>
			</StyledVocabBar>
		);
	}

	return (
		<StyledVocabBar
			$isPortrait={isPortrait}
			className="vocab-bar"
			role="toolbar"
			aria-label={text.vocabBar.barLabel}
		>
			<StyledTokenContainer
				ref={containerRef}
				onScroll={handleScroll}
				$isPortrait={isPortrait}
				$fadeAmount={fadeAmount}
			>
				{modifierTokens}
			</StyledTokenContainer>
		</StyledVocabBar>
	);
}

export default memo(VocabBar);
